/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

import { FiMapPin } from 'react-icons/fi'

import logger from '@/utils/logger'

const log = logger({ category: 'AddressAutocomplete' })

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

/** Below this the results are noise and every keystroke costs a request. */
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 300

export interface GeocodedPlace {
  /** The formatted address Mapbox matched. */
  address: string
  latitude: number
  longitude: number
}

interface MapboxFeature {
  properties?: {
    mapbox_id?: string
    name?: string
    place_formatted?: string
    full_address?: string
    coordinates?: { longitude: number; latitude: number }
  }
}

interface Suggestion extends GeocodedPlace {
  key: string
  /** First line of the menu entry — the place itself. */
  name: string
  /** Second line — the surrounding locality. */
  context: string
}

const toSuggestion = (feature: MapboxFeature, index: number): Suggestion | null => {
  const props = feature.properties
  const coordinates = props?.coordinates
  if (!props || !coordinates) return null
  const name = props.name ?? props.full_address ?? ''
  if (!name) return null
  return {
    key: props.mapbox_id ?? `${name}-${index}`,
    name,
    context: props.place_formatted ?? '',
    address: props.full_address ?? [name, props.place_formatted].filter(Boolean).join(', '),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  }
}

export interface AddressAutocompleteProps {
  label: string
  value: string
  /** Set once a suggestion has been chosen; cleared when the text is edited again. */
  coordinates: { latitude: number | null; longitude: number | null }
  onChange: (text: string) => void
  onSelect: (place: GeocodedPlace) => void
  placeholder?: string
  required?: boolean
  error?: string
  id?: string
  /**
   * field (default): a form field with its label and the resolved coordinates under it.
   * search: a small search box (a map's "fly to an address"), label kept for screen readers only.
   */
  variant?: 'field' | 'search'
}

/**
 * Address lookup backed by the Mapbox Geocoding API. Typing filters; picking a
 * result is what produces the coordinates the shipment row needs — free text
 * alone leaves the field unresolved, which the form treats as incomplete.
 *
 * Note for whoever revisits the Mapbox plan: results are persisted in the
 * shipment table, which is what Mapbox calls permanent geocoding. The v6
 * endpoint takes a `permanent=true` flag for that, but it 403s unless the
 * account is entitled, so it is deliberately not sent here.
 */
export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label,
  value,
  coordinates,
  onChange,
  onSelect,
  placeholder,
  required,
  error,
  id,
  variant = 'field',
}) => {
  const isSearch = variant === 'search'
  const autoId = useId()
  const inputId = id ?? autoId
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  /**
   * Only a keystroke should trigger a lookup. Searching on every `value` change
   * would also fire for values this component did not originate — the address
   * an editing form loads in, or the text written back by a pick — popping a
   * menu of partial matches over a field the user has not touched.
   */
  const userTyped = useRef(false)

  const resolved = coordinates.latitude !== null && coordinates.longitude !== null

  useEffect(() => {
    if (!userTyped.current) return
    const query = value.trim()
    if (!MAPBOX_TOKEN || query.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(() => {
      setIsSearching(true)
      setSearchError('')
      const url =
        `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(query)}` +
        `&autocomplete=true&limit=5&access_token=${MAPBOX_TOKEN}`
      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Mapbox responded ${response.status}`)
          return response.json() as Promise<{ features?: MapboxFeature[] }>
        })
        .then((body) => {
          const results = (body.features ?? []).map(toSuggestion).filter((s): s is Suggestion => s !== null)
          setSuggestions(results)
          setIsOpen(true)
        })
        .catch((ex: unknown) => {
          if (controller.signal.aborted) return
          log.error(ex)
          setSuggestions([])
          setSearchError('Address lookup is unavailable')
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsSearching(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [value])

  const handlePick = useCallback(
    (suggestion: Suggestion) => {
      // The pick writes the address back into `value`; that is not typing, so
      // it must not re-open the menu it just closed.
      userTyped.current = false
      setIsOpen(false)
      setSuggestions([])
      onSelect({ address: suggestion.address, latitude: suggestion.latitude, longitude: suggestion.longitude })
    },
    [onSelect]
  )

  return (
    <div className="form-control w-full">
      <label htmlFor={inputId} className={isSearch ? 'sr-only' : 'label-text mb-1 block'}>
        {label} {required && <span className="text-error">*</span>}
      </label>
      <div className="relative">
        <input
          id={inputId}
          className={`input input-bordered w-full ${isSearch ? 'input-sm' : ''} ${error ? 'input-error' : ''}`}
          value={value}
          onChange={(e) => {
            userTyped.current = true
            onChange(e.target.value)
          }}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          // A click on a suggestion blurs the input first, so let it land.
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          data-testid={`${inputId}-input`}
        />
        {isSearching && (
          <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2 opacity-60" />
        )}
        {isOpen && suggestions.length > 0 && (
          <ul
            role="listbox"
            className="menu absolute z-30 mt-1 w-full rounded-lg border border-base-300 bg-base-100 p-1 shadow-lg"
          >
            {suggestions.map((suggestion) => (
              <li key={suggestion.key}>
                <button
                  type="button"
                  className="flex flex-col items-start gap-0 text-left"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(suggestion)}
                >
                  <span className="text-sm">{suggestion.name}</span>
                  {suggestion.context && <span className="text-xs opacity-60">{suggestion.context}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isSearch ? null : resolved ? (
        <span className="label-text-alt mt-1 flex items-center gap-1 text-success">
          <FiMapPin className="h-3 w-3" />
          <span>{`${coordinates.latitude?.toFixed(5)}, ${coordinates.longitude?.toFixed(5)}`}</span>
        </span>
      ) : (
        <span className="label-text-alt mt-1 block text-base-content/60">
          {MAPBOX_TOKEN
            ? 'Start typing and pick a match — that is what sets the coordinates.'
            : 'VITE_MAPBOX_TOKEN is not configured, so addresses cannot be looked up.'}
        </span>
      )}
      {(error || searchError) && <span className="label-text-alt mt-1 block text-error">{error || searchError}</span>}
    </div>
  )
}
